import dagre from 'dagre';
import type { Node, Edge } from '@xyflow/react';

// Default node dimensions - larger to prevent overlaps
const DEFAULT_NODE_WIDTH = 200;
const DEFAULT_NODE_HEIGHT = 100;

export type LayoutDirection = 'TB' | 'LR' | 'BT' | 'RL';

interface LayoutOptions {
  direction?: LayoutDirection;
  nodeSpacing?: number;
  rankSpacing?: number;
}

/**
 * Calculate optimal handle positions based on node positions
 * Returns the best sourceHandle and targetHandle IDs for an edge
 */
export function getOptimalHandles(
  sourceNode: Node,
  targetNode: Node
): { sourceHandle: string; targetHandle: string } {
  const sourcePos = sourceNode.position;
  const targetPos = targetNode.position;

  // Calculate center positions
  const sourceWidth = sourceNode.measured?.width || DEFAULT_NODE_WIDTH;
  const sourceHeight = sourceNode.measured?.height || DEFAULT_NODE_HEIGHT;
  const targetWidth = targetNode.measured?.width || DEFAULT_NODE_WIDTH;
  const targetHeight = targetNode.measured?.height || DEFAULT_NODE_HEIGHT;

  const sourceCenterX = sourcePos.x + sourceWidth / 2;
  const sourceCenterY = sourcePos.y + sourceHeight / 2;
  const targetCenterX = targetPos.x + targetWidth / 2;
  const targetCenterY = targetPos.y + targetHeight / 2;

  // Calculate angle between nodes
  const dx = targetCenterX - sourceCenterX;
  const dy = targetCenterY - sourceCenterY;
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);

  // Determine best handles based on angle
  // Angle ranges: Right: -45 to 45, Bottom: 45 to 135, Left: 135 to -135, Top: -135 to -45
  let sourceHandle: string;
  let targetHandle: string;

  if (angle >= -45 && angle < 45) {
    // Target is to the RIGHT of source
    sourceHandle = 'right-source';
    targetHandle = 'left-target';
  } else if (angle >= 45 && angle < 135) {
    // Target is BELOW source
    sourceHandle = 'bottom-source';
    targetHandle = 'top-target';
  } else if (angle >= 135 || angle < -135) {
    // Target is to the LEFT of source
    sourceHandle = 'left-source';
    targetHandle = 'right-target';
  } else {
    // Target is ABOVE source (angle >= -135 && angle < -45)
    sourceHandle = 'top-source';
    targetHandle = 'bottom-target';
  }

  return { sourceHandle, targetHandle };
}

/**
 * Update edges with optimal handle positions based on current node positions
 */
export function updateEdgeHandles(nodes: Node[], edges: Edge[]): Edge[] {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  return edges.map(edge => {
    const sourceNode = nodeMap.get(edge.source);
    const targetNode = nodeMap.get(edge.target);

    if (!sourceNode || !targetNode) {
      return edge;
    }

    const { sourceHandle, targetHandle } = getOptimalHandles(
      sourceNode,
      targetNode
    );

    return {
      ...edge,
      sourceHandle,
      targetHandle,
    };
  });
}

/**
 * Apply automatic layout to nodes and edges using Dagre
 * Returns new positions for nodes and optimal handles for edges
 */
export function getLayoutedElements(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): { nodes: Node[]; edges: Edge[] } {
  const { direction = 'TB', nodeSpacing = 150, rankSpacing = 200 } = options;

  // Create a new dagre graph
  const g = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));

  // Set graph direction and spacing
  // nodesep: horizontal spacing between nodes in the same rank
  // ranksep: vertical spacing between ranks (layers)
  // edgesep: spacing between edges
  g.setGraph({
    rankdir: direction,
    nodesep: nodeSpacing,
    ranksep: rankSpacing,
    edgesep: 50,
    marginx: 50,
    marginy: 50,
  });

  // Add nodes to dagre graph
  nodes.forEach(node => {
    const width = node.measured?.width || DEFAULT_NODE_WIDTH;
    const height = node.measured?.height || DEFAULT_NODE_HEIGHT;
    g.setNode(node.id, { width, height });
  });

  // Add edges to dagre graph
  edges.forEach(edge => {
    g.setEdge(edge.source, edge.target);
  });

  // Calculate layout
  dagre.layout(g);

  // Apply new positions to nodes
  const layoutedNodes = nodes.map(node => {
    const nodeWithPosition = g.node(node.id);
    const width = node.measured?.width || DEFAULT_NODE_WIDTH;
    const height = node.measured?.height || DEFAULT_NODE_HEIGHT;

    return {
      ...node,
      position: {
        // Dagre returns center position, convert to top-left
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  // Update edge handles based on new node positions
  const layoutedEdges = updateEdgeHandles(layoutedNodes, edges);

  return {
    nodes: layoutedNodes,
    edges: layoutedEdges,
  };
}

/**
 * Get layout direction label for UI
 */
export function getLayoutDirectionLabel(direction: LayoutDirection): string {
  switch (direction) {
    case 'TB':
      return 'Top to Bottom';
    case 'BT':
      return 'Bottom to Top';
    case 'LR':
      return 'Left to Right';
    case 'RL':
      return 'Right to Left';
    default:
      return 'Top to Bottom';
  }
}
